import { core, type Json } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
import { type FabricError } from '../fabric-error/index.js';
import { jsonParse } from '../utils/parse.js';

/**
 * 文件上传输入报文
 */
export type FabricHttpFileUploadOptions = {
  /**
   * 开发者服务器地址
   */
  url: string;
  /**
   * 文件对应的 key，开发者在服务端可以通过这个 key 获取文件的二进制内容
   */
  name: string;
  /**
   * 要上传文件资源的路径 (本地路径), 可以传多少张图
   * http://temp/sfs23423042.png
   */
  filePath: string[];
  /**
   * HTTP 请求中其他额外的 form data
   */
  formData?: Record<string, string>;
  /**
   * HTTP 请求 Header，Header 中不能设置 Referer
   */
  headers?: Record<string, string>;
  /**
   * 超时时间，单位为毫秒
   */
  timeout?: number;
};

/**
 * 文件上传接收报文
 */
export type FabricHttpFileUploadCallbackData<T extends Json> = {
  /**
   * 开发者服务器返回的数据, 只有在prgress = 100 的时候才有值
   */
  data?: T;
  /**
   * 发者服务器返回的 HTTP 状态码
   */
  statusCode?: number;
  /**
   * 上传进度0-100, 100:标识当前文件上传完成,
   * 此处一定会等于100, 并且==100的时候Native执行callback complete清理内存
   */
  progress: number;
};

/**
 * 当前文件上传的状态
 */
export type FabricHttpUploadFileStatus = 'uploading' | 'complete';

export type FabricHttpUploadFileApi = <Return extends Json = Json>(
  options: FabricHttpFileUploadOptions,
  callback?: (
    err: FabricError | null,
    status: FabricHttpUploadFileStatus,
    result: FabricHttpFileUploadCallbackData<Return>
  ) => void
) => void;

export const uploadFile: FabricHttpUploadFileApi = <Return extends Json = Json>(
  options: FabricHttpFileUploadOptions,
  callback?: (
    err: FabricError | null,
    status: FabricHttpUploadFileStatus,
    result: FabricHttpFileUploadCallbackData<Return>
  ) => void
) => {
  core.call<
    'fabric_http.uploadFile',
    FabricHttpFileUploadOptions,
    FabricHttpFileUploadCallbackData<string>
  >(
    'fabric_http.uploadFile',
    {
      ...options,
      formData: options.formData || {},
    },
    callback
      ? (result) => {
          const fabricErrors = filterFabricErrors(
            result.code,
            result.message,
            result.data
          );
          callback(
            fabricErrors || null,
            result.data.progress !== 100 ? 'uploading' : 'complete',
            {
              ...result.data,
              data: jsonParse(result.data.data || '{}'),
            }
          );
        }
      : undefined
  );
};
